/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCurrentUserId } from 'modules/Auth/hooks';
import { queryKeys, useInfiniteQueryTE } from 'utils/react-query';
import { either, noop, pipe } from 'utils/fp';
import { reactLeftToJSX } from 'utils/uiStates/uiStates';
import { Column, Table, Timestamp } from 'components/Table';
import { IconDotsMenuVertical } from 'components/Icons';
import { makeFetchWorkflows, makeWorkflowUrl } from 'modules/Workflows/listingStore/createWorkflowsList';
import { taskEither } from 'fp-ts';
import { makeAppsUrl } from 'modules/Apps/listingStore/createAppsList';
import { Link } from 'components/Link';
import { link } from 'modules/Home/components/SetupComputeModal.styles';
import { HomeCardId, RESOURCES_PER_PAGE_TABLE, mapIdToTitle } from '../constants';
import { HomeCard } from '../components/HomeCard';

export function MyWorkflows() {
  const userOrOrgId = useCurrentUserId();

  const {
    data,
    isFetching,
    hasNextPage = false,
    fetchNextPage,
    isLoading,
    refetch,
  } = useInfiniteQueryTE(
    [queryKeys.Workflows, { userOrOrgId }],
    ({ pageParam = 1 }) =>
      pipe(
        makeFetchWorkflows(userOrOrgId)(`page=${pageParam}&per_page=${RESOURCES_PER_PAGE_TABLE}`),
        taskEither.map((response) => {
          if ('list' in response) {
            return response.list;
          }
          return response;
        }),
      ),
    {
      retry: false,
      enabled: !!userOrOrgId,
      getNextPageParam: (lastPage_, allPages) =>
        pipe(
          lastPage_,
          either.fold(
            () => undefined,
            (lastPage): number | undefined => (lastPage.length < RESOURCES_PER_PAGE_TABLE ? undefined : allPages.length + 1),
          ),
        ),
    },
  );

  if (!userOrOrgId) {
    return null;
  }

  return (
    <HomeCard title={mapIdToTitle[HomeCardId.MyWorkflows]} description="Combine models into chains and execute them in a single flow.">
      {pipe(
        data,
        either.fold(reactLeftToJSX(), (pages) => (
          <Table<CF.API.Workflows.Workflow>
            tableOptions={{
              columns: makeColumns(true, noop, userOrOrgId, refetch),
              data: [],
            }}
            infinite={{ pages, isFetching, hasNextPage: Boolean(hasNextPage), fetchNextPage }}
            isInitializing={isLoading}
            maxHeight={300}
            noRecordsText="No workflows found"
            noMoreItemsText="No more workflows"
          />
        )),
      )}
    </HomeCard>
  );
}

function makeColumns(
  hasAccess: boolean,
  handleDeleteWorkflow: (appId: string, workflowId: string, onComplete?: () => void) => void,
  userOrOrgId: string,
  refetch: () => void,
) {
  return [
    {
      accessorKey: 'id',
      header: 'Workflow Name',
      enableColumnFilter: false,
      cell: (props) => {
        return (
          <Link
            className={link}
            href={makeWorkflowUrl({
              entityId: props?.row?.getValue('id'),
              userId: userOrOrgId,
              appId: props?.row?.getValue('app_id'),
            })}
          >
            {props.getValue()}
          </Link>
        );
      },
    },
    {
      accessorKey: 'app_id',
      header: 'App Name',
      enableColumnFilter: false,
      cell: (props) => {
        return (
          <Link className={link} href={makeAppsUrl({ entityId: props?.row?.getValue('app_id'), userId: userOrOrgId })}>
            {props.getValue()}
          </Link>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 250,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'created_at',
      header: 'Created Date',
      cell: (props) => <Timestamp {...props} value={props.getValue() as string} />,
      enableColumnFilter: false,
      meta: {
        sortMetaData: {
          sortAscLabel: 'Sort (oldest-latest)',
          sortDescLabel: 'Sort (latest-oldest)',
        },
      },
    },
    // ...(hasAccess
    //   ? [
    //       {
    //         accessorKey: 'actions' as const,
    //         header: 'Actions',
    //         enableColumnFilter: false,
    //         enableSorting: false,
    //         size: 50,
    //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //         // @ts-ignore
    //         cell: (props) => <IconDotsMenuVertical size={18} color="#808080" />,
    //       },
    //     ]
    //   : []),
  ] as Column<CF.API.Workflows.Workflow>[];
}
